/** A canonical Flow Analytics event-time interval. */
export interface FlowWindow {
  startTime: string;
  endTime: string;
}

/**
 * Converts a client-side time to the whole-second UTC format used by Flow
 * Analytics. The service applies the same truncation for non-UI clients.
 */
export const normalizeFlowTimestamp = (value: string | Date): string | null => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const wholeSecond = new Date(Math.floor(date.getTime() / 1_000) * 1_000);
  return wholeSecond.toISOString().replace(/\.000Z$/, 'Z');
};

/** Returns a valid whole-second range, or null when its canonical bounds do not form an interval. */
export const normalizeFlowWindow = (window: FlowWindow): FlowWindow | null => {
  const startTime = normalizeFlowTimestamp(window.startTime);
  const endTime = normalizeFlowTimestamp(window.endTime);
  if (!startTime || !endTime || Date.parse(endTime) <= Date.parse(startTime)) return null;
  return { startTime, endTime };
};

/** Builds a whole-second Flow Analytics window ending at the supplied instant. */
export const createFlowWindow = (hours = 1, now = new Date()): FlowWindow => {
  const window = normalizeFlowWindow({
    startTime: new Date(now.getTime() - hours * 60 * 60 * 1_000).toISOString(),
    endTime: now.toISOString()
  });
  if (!window) throw new Error('Flow Analytics window must have a positive duration.');
  return window;
};
